import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../app/hooks";
import { formatMoney } from "../../features/billing/utils/money";
import { PaymentShape } from "../../features/payments/models/PaymentShape";
import { PaymentStatus } from "../../features/payments/models/PaymentStatus";
import { PortalPaymentDocumentKind } from "../../features/portal/enums/PortalPaymentDocumentKind";
import { PortalPayment } from "../../features/portal/models/PortalPayment";
import {
  downloadPortalDocument,
  payPortalPayment,
  PortalPayFailure,
} from "../../features/portal/slice-extensions/portal-money-slice-extension";
import { PortalPaymentAction } from "../../features/portal/utils/portalPaymentAction";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";
import { fallbackPaymentStatusStyle, paymentStatusStyles } from "./paymentStatusStyles";

interface PortalPaymentRowProps {
  memberId: string;
  payment: PortalPayment;
  action: PortalPaymentAction;
  onSetUpDebit: () => void;
  /** After a refused pay: reload the list so the row shows what the server knows. */
  onStale: () => void;
}

const DOCUMENT_STATUSES: PaymentStatus[] = [PaymentStatus.Invoiced, PaymentStatus.Overdue, PaymentStatus.Received];

/** One Payment on Money: what it is, the amount, when it is due, its state, one action and its document. */
const PortalPaymentRow = ({ memberId, payment, action, onSetUpDebit, onStale }: PortalPaymentRowProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatDate = (date: string) =>
    new Intl.DateTimeFormat(i18n.language, { day: "numeric", month: "short", year: "numeric" }).format(
      new Date(`${date}T00:00:00`),
    );

  const payFailedText = (reason: PortalPayFailure): string => {
    switch (reason) {
      case "not-payable":
        return t("portal.money.payFailed.notPayable");
      case "pending":
        return t("portal.money.payFailed.pending");
      case "mandate-missing":
        return t("portal.money.payFailed.mandateMissing");
      default:
        return t("portal.money.payFailed.failed");
    }
  };

  const handlePay = async () => {
    setError(null);
    setPaying(true);
    const result = await dispatch(payPortalPayment({ memberId, paymentId: payment.id }));
    setPaying(false);
    if (payPortalPayment.rejected.match(result)) {
      setError(payFailedText(result.payload ?? "failed"));
      if (result.payload !== "failed") onStale();
    }
  };

  const handleDownload = async () => {
    setError(null);
    const result = await dispatch(downloadPortalDocument({ memberId, paymentId: payment.id }));
    if (downloadPortalDocument.rejected.match(result)) setError(t("portal.money.downloadFailed"));
  };

  const shapeLabel = (shape: PaymentShape): string => {
    switch (shape) {
      case PaymentShape.PackPurchase:
        return t("portal.money.shape.PackPurchase");
      case PaymentShape.Subscription:
        return t("portal.money.shape.Subscription");
      case PaymentShape.VisitAccount:
        return t("portal.money.shape.VisitAccount");
      default:
        return t("portal.money.shape.Standalone");
    }
  };

  const title =
    payment.billingPeriodStart && payment.billingPeriodEnd
      ? `${shapeLabel(payment.shape)} · ${formatDate(payment.billingPeriodStart)} – ${formatDate(payment.billingPeriodEnd)}`
      : shapeLabel(payment.shape);
  const documentLabel = payment.document
    ? `${payment.document.kind === PortalPaymentDocumentKind.Receipt ? t("portal.money.receipt") : t("portal.money.invoice")} #${String(payment.document.number).padStart(5, "0")}`
    : null;
  const expected = payment.pendingDebit?.expectedSettlementDate;

  return (
    <li className="with-border rounded-2xl bg-white p-3 dark:bg-primary-20" data-testid="portal-payment-row">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-grey-20 dark:text-white">{title}</p>
          <p className="text-sm text-grey-40 dark:text-grey-80">
            {formatMoney(payment.amountMinorUnits, payment.amountCurrency, getDisplayLocale())}
            {payment.dueDate && ` · ${t("portal.money.due", { date: formatDate(payment.dueDate) })}`}
          </p>
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
            paymentStatusStyles[payment.status] ?? fallbackPaymentStatusStyle
          }`}
        >
          {t(`portal.paymentStatus.${payment.status}`, payment.status)}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
        {action === "confirm-and-pay" && payment.payLinkUrl && (
          <>
            <span className="text-grey-40 dark:text-grey-80">{t("portal.mandate.needsMember")}</span>
            <a className="btn btn-secondary" href={payment.payLinkUrl}>
              {t("portal.mandate.payLink")}
            </a>
          </>
        )}
        {action === "pay" && (
          <button type="button" className="btn btn-secondary" disabled={paying} onClick={handlePay}>
            {t("portal.money.payNow")}
          </button>
        )}
        {action === "set-up-debit" && (
          <button type="button" className="text-primary-40 hover:underline dark:text-primary-90" onClick={onSetUpDebit}>
            {t("portal.money.setUpDebit")}
          </button>
        )}
        {action === "pending" && (
          <span className="text-grey-40 dark:text-grey-80">
            {expected
              ? t("portal.money.debitStarted", { date: formatDate(expected) })
              : t("portal.money.debitStartedNoDate")}
          </span>
        )}
        {documentLabel ? (
          <button type="button" className="text-primary-40 hover:underline dark:text-primary-90" onClick={handleDownload}>
            {documentLabel}
          </button>
        ) : (
          DOCUMENT_STATUSES.includes(payment.status) && (
            <span className="text-grey-40 dark:text-grey-80">{t("portal.money.notIssued")}</span>
          )
        )}
      </div>
      {error && <p className="mt-1 text-sm font-medium text-red-700 dark:text-red-400">{error}</p>}
    </li>
  );
};

export default PortalPaymentRow;
